import { useState } from "react";
import { Popover, MenuItem, IconButton, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/redux/actions/toast-actions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  setProductCategoryAsDefault,
  toggleProductCategory,
} from "@/services/pricing-config.service";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export function CategoryActionsPopover({ category, onEditClick }) {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [setDefaultOpen, setSetDefaultOpen] = useState(false);
  const isOpen = Boolean(anchorEl);
  const isActive = category?.isActive;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggleMutation = useMutation({
    mutationFn: () =>
      toggleProductCategory({ id: category.id, isActive: !isActive }),
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Updated successfully", "success"));
      queryClient.invalidateQueries({ queryKey: ["product-categories"] });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to update", "error"));
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: () => setProductCategoryAsDefault({ id: category.id }),
    onSuccess: (data) => {
      dispatch(
        showToast(data?.message ?? "Set as default successfully", "success"),
      );
      queryClient.invalidateQueries({ queryKey: ["product-categories"] });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to set as default", "error"));
    },
  });

  const handleSetDefaultClick = () => {
    handleClose();
    setSetDefaultOpen(true);
  };

  const handleSetDefaultDialogClose = () => {
    setSetDefaultOpen(false);
    setDefaultMutation.reset();
  };

  const handleDeactivateClick = () => {
    handleClose();
    setDeactivateOpen(true);
  };

  const handleDialogClose = () => {
    setDeactivateOpen(false);
    toggleMutation.reset();
  };

  const handleViewUsers = () => {
    handleClose();

    navigate(`users/${category.id}`);
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: "8px", mt: 0.5 },
          },
        }}
      >
        <PopoverContent>
          <StyledMenuItem
            onClick={() => {
              handleClose();
              onEditClick(category);
            }}
          >
            Update
          </StyledMenuItem>
          <StyledMenuItem onClick={handleViewUsers}>View Users</StyledMenuItem>
          {!category?.isDefault && (
            <>
              <StyledMenuItem onClick={handleSetDefaultClick}>
                Set as Default
              </StyledMenuItem>
              <Divider sx={{ my: 0.5 }} />
            </>
          )}
          {isActive ? (
            <StyledMenuItem onClick={handleDeactivateClick} danger>
              Deactivate Category
            </StyledMenuItem>
          ) : (
            <StyledMenuItem onClick={handleDeactivateClick}>
              Activate Category
            </StyledMenuItem>
          )}
        </PopoverContent>
      </Popover>

      <CustomConfirmDialog
        open={setDefaultOpen}
        onClose={handleSetDefaultDialogClose}
        title="Set as Default Category"
        description={`Are you sure you want to set "${category?.name ?? "this category"}" as the default category?`}
        confirmLabel="Set as Default"
        variant="warning"
        mutation={setDefaultMutation}
        onConfirm={() => setDefaultMutation.mutate()}
      />

      <CustomConfirmDialog
        open={deactivateOpen}
        onClose={handleDialogClose}
        title={isActive ? "Deactivate Category" : "Activate Category"}
        description={
          isActive
            ? `Are you sure you want to deactivate ${category?.name ?? "this category"}?`
            : `Are you sure you want to activate ${category?.name ?? "this category"}?`
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        variant={isActive ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() => toggleMutation.mutate()}
      />
    </>
  );
}
